import * as BABYLON from '@babylonjs/core'
import { createDisposerBag, DisposerBag } from '../../sceneKit/disposerBag'
import { buildHdriSky } from '../../sceneKit/hdriSky'
import { buildRoomShell } from '../../sceneKit/roomShell'
import { buildPbrMaterialFromSet } from '../../sceneKit/pbrTextureSet'
import { hangWallPrints } from '../../sceneKit/wallPrints'
import { buildWalkCamera } from '../../sceneKit/walkCamera'
import { createVegetationSpawner } from './realVegetation'

const MODELS = '/assets/mountain-lot/models'

const HDRI_SKY_URL = '/assets/mountain-lot/env/fouriesburg_mountain_lookout_2k.hdr'

const TEX = '/assets/mountain-lot/textures'
function textureSet(set: string) {
  return {
    colorUrl: `${TEX}/${set}/${set}_1K-JPG_Color.jpg`,
    normalUrl: `${TEX}/${set}/${set}_1K-JPG_NormalGL.jpg`,
    roughnessUrl: `${TEX}/${set}/${set}_1K-JPG_Roughness.jpg`,
    aoUrl: `${TEX}/${set}/${set}_1K-JPG_AmbientOcclusion.jpg`,
  }
}

export interface LotPhoto {
  url: string
  ratio: number
}

export interface MountainLotApi {
  dispose: () => void
}

const W = 9.5
const D = 9.5
const H = 4.4
const EYE_HEIGHT = 1.62
const MARGIN = 0.5

function dotTexture(scene: BABYLON.Scene, name: string, size = 64) {
  const tex = new BABYLON.DynamicTexture(name, { width: size, height: size }, scene, false)
  const ctx = tex.getContext() as unknown as CanvasRenderingContext2D
  const r = size / 2
  const grad = ctx.createRadialGradient(r, r, 0, r, r, r)
  grad.addColorStop(0, 'rgba(255,255,255,0.9)')
  grad.addColorStop(1, 'rgba(255,255,255,0)')
  ctx.fillStyle = grad
  ctx.fillRect(0, 0, size, size)
  tex.update(false)
  tex.hasAlpha = true
  return tex
}

const TERRAIN_HEIGHTMAP_URL = '/assets/mountain-lot/heightmap.png'
const TERRAIN_SIZE = 260

/**
 * Real terrain mesh in place of the old scattered cone "peaks" — one
 * `CreateGroundFromHeightMap` sculpted from a procedural fractal-noise PNG,
 * masked to the same front/right visible arc the cone scatter used to
 * cover. The heightmap's alpha channel carves holes everywhere outside that
 * arc, so the flat grass plane still shows through behind the solid walls.
 */
function buildMountainTerrain(scene: BABYLON.Scene, bag: DisposerBag, material: BABYLON.Material) {
  const terrain = bag.track(
    BABYLON.MeshBuilder.CreateGroundFromHeightMap(
      'lotTerrain',
      TERRAIN_HEIGHTMAP_URL,
      // alphaFilter is what actually turns the PNG's alpha channel into holes — without it
      // (default 0) every vertex passes the `a >= alphaFilter` check and the "empty" areas
      // still render as a flat rock slab at minHeight, right through the cabin floor.
      { width: TERRAIN_SIZE, height: TERRAIN_SIZE, subdivisions: 140, minHeight: 0, maxHeight: 42, alphaFilter: 0.5 },
      scene,
    ),
  )
  terrain.material = material
  terrain.isPickable = false
  terrain.applyFog = false
  terrain.receiveShadows = true
  return terrain
}

/**
 * A first-person "Mountain Lot" — floor-to-ceiling glass on two sides
 * looking out at a real alpine vista (HDRI sky, a heightmap-sculpted
 * mountain terrain, real fir/boulder scans, a painted waterfall), solid
 * walls + a hearth on the other two, all real PBR-textured. WASD/arrows
 * walk (Babylon's `UniversalCamera` keys, same as Studio's own Walk view);
 * position gets clamped to the interior footprint every frame so there's no
 * way to walk (or wheel-zoom — none is bound) through the glass and out
 * into the scenery.
 */
export function buildMountainLot(scene: BABYLON.Scene, canvas: HTMLCanvasElement, photos: LotPhoto[]): MountainLotApi {
  const bag = createDisposerBag()

  scene.clearColor = BABYLON.Color4.FromHexString('#bcd6e0ff')
  scene.fogMode = BABYLON.Scene.FOGMODE_EXP2
  scene.fogDensity = 0.0055
  scene.fogColor = BABYLON.Color3.FromHexString('#cfe0e6')

  /* ---------- sky + sun ---------- */
  // Real photographed alpine panorama instead of a painted gradient — it also becomes the
  // scene's IBL source, so the sun/fill lights below are toned down to avoid double-lighting.
  buildHdriSky(scene, bag, HDRI_SKY_URL, { domeDiameter: 480, rotationY: Math.PI * 0.15 })

  const sun = bag.track(new BABYLON.DirectionalLight('lotSun', new BABYLON.Vector3(-0.45, -0.6, 0.4), scene))
  sun.position = new BABYLON.Vector3(20, 24, -18)
  sun.intensity = 1.15
  sun.diffuse = BABYLON.Color3.FromHexString('#fff0d2')
  const shadows = bag.track(new BABYLON.ShadowGenerator(1024, sun))
  shadows.useBlurExponentialShadowMap = true
  shadows.blurKernel = 18
  shadows.darkness = 0.35

  const sky = bag.track(new BABYLON.HemisphericLight('lotSkyFill', new BABYLON.Vector3(0, 1, 0), scene))
  sky.intensity = 0.35
  sky.diffuse = BABYLON.Color3.FromHexString('#dfeaf2')
  sky.groundColor = BABYLON.Color3.FromHexString('#4a6b45')

  /* ---------- exterior ground + vista ---------- */
  const groundMat = buildPbrMaterialFromSet(scene, bag, { name: 'lotGroundMat', ...textureSet('Grass005'), uScale: 90, vScale: 90 })
  const ground = bag.track(BABYLON.MeshBuilder.CreateGround('lotGround', { width: 360, height: 360 }, scene))
  ground.position.y = -0.02
  ground.material = groundMat
  ground.receiveShadows = true

  // Visible arc covers the front glass wall (facing -z) through the right glass wall (facing +x) —
  // the heightmap's mask matches this same arc so the range only rises where it's actually seen.
  const ARC_FROM = -Math.PI * 1.06
  const ARC_TO = 0.32
  const terrainMat = buildPbrMaterialFromSet(scene, bag, { name: 'lotTerrainMat', ...textureSet('Rock051'), uScale: TERRAIN_SIZE / 6, vScale: TERRAIN_SIZE / 6 })
  buildMountainTerrain(scene, bag, terrainMat)

  // Real Poly Haven scans: a fir sapling instanced across the clearing (scaled up for
  // variety since the full-size "Fir Tree 01" scan's mesh alone runs ~480MB — nowhere
  // near web-viable, even before textures), plus a scatter of boulders for ground detail.
  const saplingSpawner = createVegetationSpawner(scene, bag, `${MODELS}/fir_sapling/`, 'fir_sapling_1k.gltf')
  const boulderSpawner = createVegetationSpawner(scene, bag, `${MODELS}/boulder_01/`, 'boulder_01_1k.gltf')
  for (let i = 0; i < 34; i++) {
    const t = i / 33
    const angle = ARC_FROM + (ARC_TO - ARC_FROM) * t + (Math.random() - 0.5) * 0.25
    const r = 9 + Math.random() * 34
    const scale = 0.85 + Math.random() * 1.35
    saplingSpawner.spawn({ x: Math.cos(angle) * r, z: Math.sin(angle) * r, scale, rotY: Math.random() * Math.PI * 2 })
  }
  for (let i = 0; i < 9; i++) {
    const angle = ARC_FROM + (ARC_TO - ARC_FROM) * Math.random()
    const r = 13 + Math.random() * 42
    const scale = 0.5 + Math.random() * 0.9
    boulderSpawner.spawn({ x: Math.cos(angle) * r, z: Math.sin(angle) * r, scale, rotY: Math.random() * Math.PI * 2 })
  }

  /* ---------- waterfall ---------- */
  const fallAngle = -Math.PI * 0.62
  const fallRadius = 46
  const fallX = Math.cos(fallAngle) * fallRadius
  const fallZ = Math.sin(fallAngle) * fallRadius
  const fallHeight = 20
  const fallTex = bag.track(new BABYLON.DynamicTexture('waterfallTex', { width: 32, height: 256 }, scene, false))
  {
    const ctx = fallTex.getContext() as unknown as CanvasRenderingContext2D
    ctx.fillStyle = '#dff3fa'
    ctx.fillRect(0, 0, 32, 256)
    for (let i = 0; i < 400; i++) {
      const x = Math.random() * 32
      const y = Math.random() * 256
      const w = 1 + Math.random() * 2
      const h = 6 + Math.random() * 20
      ctx.fillStyle = Math.random() > 0.5 ? 'rgba(255,255,255,0.85)' : 'rgba(170,210,225,0.6)'
      ctx.fillRect(x, y, w, h)
    }
    fallTex.update(false)
  }
  fallTex.wrapV = BABYLON.Texture.WRAP_ADDRESSMODE
  const fallMat = bag.track(new BABYLON.StandardMaterial('waterfallMat', scene))
  fallMat.emissiveTexture = fallTex
  fallMat.diffuseColor = BABYLON.Color3.Black()
  fallMat.specularColor = BABYLON.Color3.Black()
  fallMat.disableLighting = true
  fallMat.backFaceCulling = false
  fallMat.alpha = 0.92
  const waterfall = bag.track(BABYLON.MeshBuilder.CreatePlane('waterfall', { width: 2.6, height: fallHeight }, scene))
  waterfall.position.set(fallX, fallHeight / 2, fallZ)
  waterfall.rotation.y = fallAngle + Math.PI / 2
  waterfall.material = fallMat
  waterfall.isPickable = false
  waterfall.applyFog = false

  const mistTex = bag.track(dotTexture(scene, 'mistDot'))
  const mistMat = bag.track(new BABYLON.StandardMaterial('mistMat', scene))
  mistMat.emissiveTexture = mistTex
  mistMat.opacityTexture = mistTex
  mistMat.diffuseColor = BABYLON.Color3.Black()
  mistMat.specularColor = BABYLON.Color3.Black()
  mistMat.disableLighting = true
  mistMat.backFaceCulling = false
  const mist = bag.track(BABYLON.MeshBuilder.CreateDisc('mist', { radius: 4, tessellation: 24 }, scene))
  mist.rotation.x = Math.PI / 2
  mist.position.set(fallX, 0.4, fallZ)
  mist.material = mistMat
  mist.isPickable = false
  mist.applyFog = false

  const mistParticles = bag.track(new BABYLON.ParticleSystem('mistParticles', 60, scene))
  mistParticles.particleTexture = mistTex
  mistParticles.emitter = new BABYLON.Vector3(fallX, 0.3, fallZ)
  mistParticles.minEmitBox = new BABYLON.Vector3(-1.2, 0, -1.2)
  mistParticles.maxEmitBox = new BABYLON.Vector3(1.2, 0, 1.2)
  mistParticles.color1 = new BABYLON.Color4(1, 1, 1, 0.35)
  mistParticles.color2 = new BABYLON.Color4(1, 1, 1, 0.12)
  mistParticles.colorDead = new BABYLON.Color4(1, 1, 1, 0)
  mistParticles.minSize = 0.6
  mistParticles.maxSize = 1.8
  mistParticles.minLifeTime = 1.8
  mistParticles.maxLifeTime = 3.2
  mistParticles.emitRate = 18
  mistParticles.direction1 = new BABYLON.Vector3(-0.2, 1, -0.2)
  mistParticles.direction2 = new BABYLON.Vector3(0.2, 1.4, 0.2)
  mistParticles.minEmitPower = 0.3
  mistParticles.maxEmitPower = 0.7
  mistParticles.gravity = new BABYLON.Vector3(0, 0.15, 0)
  mistParticles.blendMode = BABYLON.ParticleSystem.BLENDMODE_ADD
  mistParticles.start()

  /* ---------- interior shell: floor, ceiling, solid back+left walls, glass front+right ---------- */
  const glassMat = bag.track(new BABYLON.PBRMaterial('lotGlassMat', scene))
  glassMat.albedoColor = new BABYLON.Color3(0.88, 0.93, 0.95)
  glassMat.metallic = 0
  glassMat.roughness = 0.05
  glassMat.alpha = 0.14
  glassMat.backFaceCulling = false
  const mullionMat = bag.track(new BABYLON.StandardMaterial('lotMullionMat', scene))
  mullionMat.diffuseColor = BABYLON.Color3.FromHexString('#171512')
  mullionMat.specularColor = BABYLON.Color3.Black()

  // Indoor surfaces get a much weaker environmentIntensity than the exterior ground/terrain —
  // full-strength reflection of the outdoor HDRI's bright sun turns a matte wood/stone finish
  // into a mirror that throws a blown-out glint straight up off the floor at grazing angles.
  const INDOOR_ENV = 0.18
  const floorMat = buildPbrMaterialFromSet(scene, bag, {
    name: 'lotFloorMat',
    ...textureSet('WoodFloor051'),
    uScale: W / 2.4,
    vScale: D / 2.4,
    environmentIntensity: INDOOR_ENV,
  })
  // These wall/ceiling planes are viewed from their "inside" face, which is the backface by
  // default — buildTemplatedRoomShell's solid walls always disabled culling for this same
  // reason; doing the same here since these are real PBRMaterials built by hand instead.
  const leftWallMat = buildPbrMaterialFromSet(scene, bag, {
    name: 'lotLeftWallMat',
    ...textureSet('Planks037A'),
    uScale: D / 2,
    vScale: H / 2,
    environmentIntensity: INDOOR_ENV,
  })
  leftWallMat.backFaceCulling = false
  leftWallMat.twoSidedLighting = true
  const backWallMat = buildPbrMaterialFromSet(scene, bag, {
    name: 'lotBackWallMat',
    ...textureSet('Rock051'),
    uScale: W / 1.8,
    vScale: H / 1.8,
    environmentIntensity: INDOOR_ENV,
  })
  backWallMat.backFaceCulling = false
  backWallMat.twoSidedLighting = true
  const ceilMat = bag.track(new BABYLON.StandardMaterial('lotCeilMat', scene))
  ceilMat.diffuseColor = BABYLON.Color3.FromHexString('#2c231a')
  ceilMat.specularColor = BABYLON.Color3.Black()
  ceilMat.backFaceCulling = false

  const shell = buildRoomShell(scene, bag, {
    width: W,
    height: H,
    depth: D,
    floorMaterial: floorMat,
    ceilMaterial: ceilMat,
    walls: {
      left: { kind: 'solid', material: leftWallMat },
      right: { kind: 'glass', material: glassMat, mullionMaterial: mullionMat, mullionEvery: 1.35 },
      back: { kind: 'solid', material: backWallMat },
      front: { kind: 'glass', material: glassMat, mullionMaterial: mullionMat, mullionEvery: 1.35 },
    },
  })
  const { floor } = shell

  const beamMat = bag.track(new BABYLON.StandardMaterial('lotBeamMat', scene))
  beamMat.diffuseColor = BABYLON.Color3.FromHexString('#1c140d')
  ;[-3, -1, 1, 3].forEach((x) => {
    const beam = BABYLON.MeshBuilder.CreateBox('beam', { width: 0.18, height: 0.22, depth: D }, scene)
    beam.position.set(x, H - 0.14, 0)
    beam.material = beamMat
    beam.isPickable = false
    bag.track(beam)
  })

  const stoneMat = buildPbrMaterialFromSet(scene, bag, { name: 'lotStoneMat', ...textureSet('Rock051'), uScale: 1.4, vScale: 3.2, environmentIntensity: INDOOR_ENV })

  // Hearth on the left wall.
  const hearth = BABYLON.MeshBuilder.CreateBox('hearth', { width: 0.5, height: 1.6, depth: 1.7 }, scene)
  hearth.position.set(-W / 2 + 0.25, 0.8, -2.6)
  hearth.material = stoneMat
  bag.track(hearth)
  const chimney = BABYLON.MeshBuilder.CreateBox('chimney', { width: 0.9, height: H, depth: 1.0 }, scene)
  chimney.position.set(-W / 2 + 0.45, H / 2, -2.6)
  chimney.material = stoneMat
  bag.track(chimney)
  const fireGlowMat = bag.track(new BABYLON.StandardMaterial('fireGlow', scene))
  fireGlowMat.emissiveColor = BABYLON.Color3.FromHexString('#ff7a3d')
  fireGlowMat.diffuseColor = BABYLON.Color3.Black()
  const fireGlow = BABYLON.MeshBuilder.CreatePlane('fireGlowPlane', { width: 0.7, height: 0.5 }, scene)
  fireGlow.position.set(-W / 2 + 0.26, 0.35, -2.6)
  fireGlow.rotation.y = Math.PI / 2
  fireGlow.material = fireGlowMat
  fireGlow.isPickable = false
  bag.track(fireGlow)
  const fireLight = bag.track(new BABYLON.PointLight('fireLight', new BABYLON.Vector3(-W / 2 + 0.6, 0.5, -2.6), scene))
  fireLight.diffuse = BABYLON.Color3.FromHexString('#ff8c4a')
  fireLight.intensity = 0.6
  fireLight.range = 5

  // A couple of gallery photos on the back wall.
  const framePhotos = photos.slice(0, 2)
  hangWallPrints(
    scene,
    bag,
    framePhotos,
    framePhotos.map((_photo, i) => ({ x: 1.6 + i * 1.9, y: 2.0, z: D / 2, wallRotY: Math.PI, width: 1.1 })),
  )

  /* ---------- camera: first-person walk, clamped to the room ---------- */
  let t = 0
  const { camera: cam } = buildWalkCamera(scene, canvas, bag, {
    name: 'lotCam',
    start: new BABYLON.Vector3(0, EYE_HEIGHT, D / 2 - 1.3),
    lookAt: new BABYLON.Vector3(0, EYE_HEIGHT, -D / 2),
    eyeHeight: EYE_HEIGHT,
    bounds: { x: W / 2 - MARGIN, z: D / 2 - MARGIN },
    fov: 0.92,
    speed: 0.12,
    onBeforeRender: () => {
      t += scene.getEngine().getDeltaTime() / 1000
      fallTex.vOffset = (t * 0.6) % 1
    },
  })

  const pipeline = bag.track(new BABYLON.DefaultRenderingPipeline('lotPipe', true, scene, [cam]))
  pipeline.fxaaEnabled = true
  pipeline.imageProcessingEnabled = true
  pipeline.imageProcessing.toneMappingEnabled = true
  pipeline.imageProcessing.toneMappingType = BABYLON.ImageProcessingConfiguration.TONEMAPPING_ACES
  pipeline.imageProcessing.exposure = 1.1
  pipeline.imageProcessing.contrast = 1.08

  ;[floor, shell.walls.back, shell.walls.left, hearth, chimney].forEach((m) => {
    if (m) shadows.addShadowCaster(m, false)
  })

  return {
    dispose: () => bag.disposeAll(),
  }
}
